import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as d3 from 'd3';
import { EmailVolumeByDay, EmailByDayOfWeek } from '~/core/services/http.service';

@Component({
    selector: 'app-email-graphs',
    templateUrl: './email-graphs.component.html',
    styleUrls: ['./email-graphs.component.scss'],
    standalone: false
})
export class EmailGraphsComponent implements OnInit, AfterViewInit, OnChanges {
    @ViewChild('emailsPerWeekChart', { static: false }) emailsPerWeekChart!: ElementRef;
    @ViewChild('emailsByDayChart', { static: false }) emailsByDayChart!: ElementRef;

    @Input() emailVolumeByDay: EmailVolumeByDay[] = [];
    @Input() emailsByDayOfWeek: EmailByDayOfWeek[] = [];

    public showEmailGraphPerWeek: boolean = true;
    public showEmailGraphByDay: boolean = true;

    constructor() {}

    ngOnInit(): void {}

    ngAfterViewInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['emailVolumeByDay'] || changes['emailsByDayOfWeek']) && this.showEmailGraphPerWeek) {
            setTimeout(() => this.renderEmailsPerWeekChart(), 100);
        }
        if (changes['emailsByDayOfWeek'] && this.showEmailGraphByDay) {
            setTimeout(() => this.renderEmailsByDayChart(), 100);
        }
    }

    /**
     * Toggle email graph sections
     */
    public toggleEmailGraph(graphType: string): void {
        if (graphType === 'emailsPerWeek') {
            this.showEmailGraphPerWeek = !this.showEmailGraphPerWeek;
            if (this.showEmailGraphPerWeek) {
                setTimeout(() => this.renderEmailsPerWeekChart(), 100);
            }
        } else if (graphType === 'emailsByDay') {
            this.showEmailGraphByDay = !this.showEmailGraphByDay;
            if (this.showEmailGraphByDay) {
                setTimeout(() => this.renderEmailsByDayChart(), 100);
            }
        }
    }

    /**
     * Aggregate email volume by week
     */
    private aggregateByWeek(data: EmailVolumeByDay[]): Array<{ week: string; emails: number }> {
        const weekMap = new Map<string, number>();
        
        data.forEach(item => {
            const date = new Date(item.date);
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay());
            const weekKey = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            
            weekMap.set(weekKey, (weekMap.get(weekKey) || 0) + item.count);
        });

        return Array.from(weekMap.entries()).map(([week, emails]) => ({ week, emails }));
    }

    /**
     * Render line chart for emails per week
     */
    private renderEmailsPerWeekChart(): void {
        if (!this.emailsPerWeekChart || this.emailVolumeByDay.length === 0) return;

        const data = this.aggregateByWeek(this.emailVolumeByDay);

        const container = this.emailsPerWeekChart.nativeElement;
        const containerWidth = container.clientWidth || 450;
        
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const margin = { top: 15, right: 20, bottom: 30, left: 50 };
        const width = (containerWidth - margin.left - margin.right) || 350;
        const height = 250;

        const svgElement = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const x = d3.scaleBand()
            .domain(data.map(d => d.week))
            .range([0, width])
            .padding(0.15);

        const y = d3.scaleLinear()
            .domain([0, d3.max(data, d => d.emails) as number])
            .range([height, 0]);

        // Line generator
        const line = d3.line<any>()
            .x(d => x(d.week) as number + x.bandwidth() / 2)
            .y(d => y(d.emails));

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisLeft(y)
                .tickSize(-width)
                .tickFormat(() => '')
            )
            .style('stroke-dasharray', '4')
            .style('stroke', '#e5e7eb')
            .style('stroke-opacity', 0.5);

        // Add X axis
        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x))
            .style('font-size', '11px');

        // Add Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .style('font-size', '11px');

        // Create tooltip
        const tooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        // Add line path
        g.append('path')
            .datum(data)
            .attr('fill', 'none')
            .attr('stroke', '#3b82f6')
            .attr('stroke-width', 2.5)
            .attr('d', line);

        // Add dots
        g.selectAll('.dot')
            .data(data)
            .enter()
            .append('circle')
            .attr('cx', d => x(d.week) as number + x.bandwidth() / 2)
            .attr('cy', d => y(d.emails))
            .attr('r', 4)
            .attr('fill', '#3b82f6')
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                tooltip.html(`Week: ${d.week}<br/>Emails: ${d.emails}`)
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)');
                d3.select(this).attr('r', 6).attr('fill', '#1d4ed8');
            })
            .on('mousemove', function(event) {
                tooltip.style('left', (event.pageX + 10) + 'px').style('top', (event.pageY + 10) + 'px');
            })
            .on('mouseout', function() {
                tooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
                d3.select(this).attr('r', 4).attr('fill', '#3b82f6');
            });
    }

    /**
     * Render bar chart for emails by day of week
     */
    private renderEmailsByDayChart(): void {
        if (!this.emailsByDayChart || this.emailsByDayOfWeek.length === 0) return;

        const data = this.emailsByDayOfWeek.map(item => ({
            day: item.day.substring(0, 3),
            emails: item.count
        }));

        const container = this.emailsByDayChart.nativeElement;
        const containerWidth = container.clientWidth || 450;

        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const margin = { top: 15, right: 20, bottom: 30, left: 50 };
        const width = (containerWidth - margin.left - margin.right) || 350;
        const height = 250;

        const svgElement = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const x = d3.scaleBand()
            .domain(data.map(d => d.day))
            .range([0, width])
            .padding(0.15);

        const y = d3.scaleLinear()
            .domain([0, d3.max(data, d => d.emails) as number])
            .range([height, 0]);

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisLeft(y)
                .tickSize(-width)
                .tickFormat(() => '')
            )
            .style('stroke-dasharray', '4')
            .style('stroke', '#e5e7eb')
            .style('stroke-opacity', 0.5);

        // Add X axis
        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x))
            .style('font-size', '11px');

        // Add Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .style('font-size', '11px');

        // Create tooltip
        const tooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        // Add bars
        g.selectAll('.bar')
            .data(data)
            .enter()
            .append('rect')
            .attr('class', 'bar')
            .attr('x', d => x(d.day) as number)
            .attr('y', d => y(d.emails))
            .attr('width', x.bandwidth())
            .attr('height', d => height - y(d.emails))
            .attr('fill', '#3b82f6')
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                tooltip.html(`Day: ${d.day}<br/>Emails: ${d.emails}`)
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)');
                d3.select(this).attr('fill', '#1d4ed8');
            })
            .on('mousemove', function(event) {
                tooltip.style('left', (event.pageX + 10) + 'px').style('top', (event.pageY + 10) + 'px');
            })
            .on('mouseout', function() {
                tooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
                d3.select(this).attr('fill', '#3b82f6');
            });
    }
}
